import { camelCaseToSnakeCase, snakeCaseToCamelCase } from './string'

export interface BaseObject {
  [key: string]: any
}

/**
 *
 * @param input Objet at camelCase
 * @returns New object with keys in snake_case
 */
export function camelCaseObjectToSnakeCase<T extends BaseObject>(input: T) {
  const output: any = {}

  for (const [key, value] of Object.entries(input)) {
    delete output[key]
    if (
      typeof value === 'object' &&
      value !== null &&
      !Array.isArray(value) &&
      !(value instanceof Date)
    ) {
      output[camelCaseToSnakeCase(key) || key] = camelCaseObjectToSnakeCase(value)
    } else {
      if (Array.isArray(value)) {
        output[camelCaseToSnakeCase(key) || key] = value.map(v => camelCaseObjectToSnakeCase(v))
      } else {
        output[camelCaseToSnakeCase(key) || key] = value
      }
    }
  }
  return output
}

/**
 *
 * @param input Object at snake_case
 * @returns New object with keys in camelCase
 */
export function snakeCaseObjectToCamelCase(input: BaseObject): BaseObject {
  const output: BaseObject = {}

  for (const [key, value] of Object.entries(input)) {
    const newKey = snakeCaseToCamelCase(key) || key

    if (
      typeof value === 'object' &&
      value !== null &&
      !Array.isArray(value) &&
      !(value instanceof Date)
    ) {
      output[newKey] = snakeCaseObjectToCamelCase(value)
    } else if (Array.isArray(value)) {
      // Apenas aplica recursão se os elementos forem objetos
      output[newKey] = value.map(v =>
        typeof v === 'object' && v !== null && !Array.isArray(v)
          ? snakeCaseObjectToCamelCase(v)
          : v,
      )
    } else {
      if (Array.isArray(value)) {
        // Se for tipo primitivo, copia
        if (value.every(v => v === null || ['string', 'number', 'boolean'].includes(typeof v))) {
          output[snakeCaseToCamelCase(key) || key] = value
        } else {
          // Se for objeto
          output[snakeCaseToCamelCase(key) || key] = value.map(v => snakeCaseObjectToCamelCase(v))
        }
      } else {
        output[snakeCaseToCamelCase(key) || key] = value
      }
    }
  }

  return output
}

export function omit<T extends Record<string, any>, K extends keyof T>(
  input: T,
  keys: K[],
): Omit<T, K> {
  const output: Partial<T> = {}

  for (const [key, value] of Object.entries(input) as [K, T[K]][]) {
    if (!keys.includes(key)) {
      output[key] = value
    }
  }
  return output as Omit<T, K>
}

export function pick<T extends Record<string, any>, K extends keyof T>(
  input: T,
  keys: K[],
): Pick<T, K> {
  const output: Partial<T> = {}

  for (const key of keys) {
    output[key] = input[key]
  }
  return output as Pick<T, K>
}
